import api from './client';

// Types for analytics
export interface MonthlyTrendItem {
  month: string;
  sales: number;
  expenses: number;
  profit: number;
}

export interface MonthlyTrends {
  trends: MonthlyTrendItem[];
  year: number;
}

export interface CategoryExpense {
  category: string;
  amount: number;
  percentage: number;
}

export interface ExpenseCategoryBreakdown {
  categories: CategoryExpense[];
  totalExpenses: number;
  startDate: string;
  endDate: string;
}

export interface CashFlowItem {
  date: string;
  inflow: number;
  outflow: number;
  balance: number;
}

export interface CashFlowAnalysis {
  cashFlow: CashFlowItem[];
  startingBalance: number;
  endingBalance: number;
  netCashFlow: number;
  startDate: string;
  endDate: string;
}

export type FinancialRatioStatus = 'good' | 'warning' | 'critical';

export interface FinancialRatio {
  name: string;
  value: number;
  benchmark: number;
  status: FinancialRatioStatus;
}

export interface FinancialHealthMetrics {
  profitMargin: number;
  quickRatio: number;
  cashRatio: number;
  returnOnInvestment: number;
  ratios: FinancialRatio[];
}

export interface SalesAnalytics {
  totalSales: number;
  averageSale: number;
}

export interface ExpenseAnalytics {
  totalExpenses: number;
  expensesByCategory: Record<string, number>;
  expensesCount: number;
}

export interface InvestmentAnalytics {
  totalInvestments: number;
  investmentsByCategory: Record<string, number>;
  investmentsCount: number;
}

export interface ProfitabilityAnalytics {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  profitMargin: number;
  monthlyBreakdown: {
    month: string;
    revenue: number;
    expenses: number;
    profit: number;
  }[];
}

export interface CustomerAnalytics {
  id: string;
  name: string;
  totalSpent: number;
  transactionCount: number;
  lastPurchase: string;
  phone?: string;
  address?: string;
}

export interface DashboardAnalytics {
  totalRevenue: number;
  totalExpenses: number;
  totalPurchases: number;
  netProfit: number;
  profitMargin: number;
  monthlyRevenue: Record<string, number>;
  monthlyExpenses: Record<string, number>;
  monthlyPurchases: Record<string, number>;
  recentTransactions: DashboardTransaction[];
  topCustomers: CustomerAnalytics[];
  expenseCategories: Record<string, number>;
  cashFlow: {
    incoming: number;
    outgoing: number;
    balance: number;
  };
  kpis: {
    revenueGrowth: number;
    customerAcquisition: number;
    avgOrderValue: number;
    monthlyRecurring: number;
  };
}

export interface DashboardTransaction {
  id: string;
  type: 'income' | 'expense';
  amount: number;
  description: string;
  category: string;
  date: string;
  status: 'completed' | 'pending' | 'failed';
}

// API functions
export const analyticsApi = {
  // Get monthly trends for a specific year
  async getMonthlyTrends(year?: number): Promise<MonthlyTrends> {
    const currentYear = year || new Date().getFullYear();
    const response = await api.get<MonthlyTrends>(`accounting/analytics/monthly-trends?year=${currentYear}`);
    return response.data;
  },

  // Get expense category breakdown
  async getExpenseCategories(startDate?: string, endDate?: string): Promise<ExpenseCategoryBreakdown> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get<ExpenseCategoryBreakdown>(`accounting/analytics/expense-categories?${params.toString()}`);
    return response.data;
  },

  // Get cash flow analysis
  async getCashFlow(startDate?: string, endDate?: string): Promise<CashFlowAnalysis> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get<CashFlowAnalysis>(`accounting/analytics/cash-flow?${params.toString()}`);
    return response.data;
  },

  // Get financial health metrics
  async getFinancialHealthMetrics(): Promise<FinancialHealthMetrics> {
    const response = await api.get<FinancialHealthMetrics>('accounting/analytics/health-metrics');
    return response.data;
  },

  // Get sales analytics
  async getSalesAnalytics(startDate?: string, endDate?: string): Promise<SalesAnalytics> {
    if (startDate && endDate) {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      const response = await api.get<SalesAnalytics>(`accounting/sales/analytics/date-range?${params.toString()}`);
      return response.data;
    } else {
      // The non-date-range endpoint returns aggregate fields with different names,
      // so normalize them into the SalesAnalytics shape.
      const response = await api.get<any>('accounting/sales/analytics');
      const data = response.data || {};
      const totalSales = Number(data.totalSalesAmount ?? data.totalSales ?? 0);
      const count = Number(data.totalSalesCount ?? 0);
      return {
        totalSales,
        averageSale: count > 0 ? totalSales / count : 0,
      };
    }
  },

  // Get expense analytics
  async getExpenseAnalytics(): Promise<ExpenseAnalytics> {
    const response = await api.get<ExpenseAnalytics>('accounting/expenses/analytics');
    return response.data;
  },

  // Get investment analytics
  async getInvestmentAnalytics(): Promise<InvestmentAnalytics> {
    const response = await api.get<any>('accounting/analytics/investments');
    const data = response.data || {};

    // Backend returns investmentsByCategory as an array of { category, total }.
    // Normalize it into the Record shape expected by the frontend.
    const investmentsByCategory: Record<string, number> = {};
    if (Array.isArray(data.investmentsByCategory)) {
      data.investmentsByCategory.forEach((item: any) => {
        if (item?.category) {
          investmentsByCategory[item.category] = Number(item.total ?? 0);
        }
      });
    } else if (data.investmentsByCategory && typeof data.investmentsByCategory === 'object') {
      Object.entries(data.investmentsByCategory).forEach(([key, value]) => {
        investmentsByCategory[key] = Number(value ?? 0);
      });
    }

    return {
      totalInvestments: Number(data.totalInvestments ?? 0),
      investmentsByCategory,
      investmentsCount: Number(
        data.investmentsCount ??
          (Array.isArray(data.investmentsByCategory) ? data.investmentsByCategory.length : 0),
      ),
    };
  },

  // Get comprehensive dashboard analytics (this will be our main function).
  // All totals, monthly breakdowns and cash flow are computed from the full
  // sales / expenses / purchases records within the selected range so the
  // figures are accurate and span multiple years where applicable.
  async getDashboardAnalytics(dateRange: '7days' | '30days' | '90days' | '12months' | '24months' | 'all' = 'all'): Promise<DashboardAnalytics> {
    const endDate = new Date();
    let startDate = new Date();

    switch (dateRange) {
      case '7days':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case '30days':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case '90days':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case '12months':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      case '24months':
        startDate.setFullYear(endDate.getFullYear() - 2);
        break;
      case 'all':
        startDate = new Date('2000-01-01'); // Effectively all historical data
        break;
    }

    const emptyDashboard: DashboardAnalytics = {
      totalRevenue: 0,
      totalExpenses: 0,
      totalPurchases: 0,
      netProfit: 0,
      profitMargin: 0,
      monthlyRevenue: {},
      monthlyExpenses: {},
      monthlyPurchases: {},
      recentTransactions: [],
      topCustomers: [],
      expenseCategories: {},
      cashFlow: { incoming: 0, outgoing: 0, balance: 0 },
      kpis: { revenueGrowth: 0, customerAcquisition: 0, avgOrderValue: 0, monthlyRecurring: 0 },
    };

    try {
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];
      const range = `startDate=${start}&endDate=${end}`;

      // Fetch the full record sets for the period (the list endpoints return
      // every matching row; they do not paginate).
      const [salesRes, expensesRes, purchasesRes] = await Promise.all([
        api.get<any>(`accounting/sales?${range}`),
        api.get<any>(`accounting/expenses?${range}`),
        api.get<any>(`accounting/purchases?${range}`),
      ]);

      const sales: any[] = Array.isArray(salesRes.data) ? salesRes.data : [];
      const expenses: any[] = Array.isArray(expensesRes.data) ? expensesRes.data : [];
      const purchases: any[] = Array.isArray(purchasesRes.data) ? purchasesRes.data : [];

      const saleAmount = (s: any) => Number(s.total ?? s.totalAmount ?? 0);
      const expenseAmount = (e: any) => Number(e.amount ?? 0);
      const purchaseAmount = (p: any) => Number(p.total ?? p.totalAmount ?? 0);

      // Totals
      const totalRevenue = sales.reduce((sum, s) => sum + saleAmount(s), 0);
      const totalExpenses = expenses.reduce((sum, e) => sum + expenseAmount(e), 0);
      const totalPurchases = purchases.reduce((sum, p) => sum + purchaseAmount(p), 0);
      const netProfit = totalRevenue - totalExpenses;
      const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

      // Monthly breakdown (multi-year aware). Keyed by a chronological sort key
      // but surfaced with a human label like "Jan 2025".
      const monthlyMap = new Map<string, { label: string; revenue: number; expenses: number; purchases: number }>();
      const monthBucket = (rawDate: string) => {
        const d = new Date(rawDate);
        const sortKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyMap.has(sortKey)) {
          monthlyMap.set(sortKey, {
            label: d.toLocaleString('en-US', { month: 'short', year: 'numeric' }),
            revenue: 0,
            expenses: 0,
            purchases: 0,
          });
        }
        return monthlyMap.get(sortKey)!;
      };

      sales.forEach((s) => { monthBucket(s.saleDate || s.createdAt).revenue += saleAmount(s); });
      expenses.forEach((e) => { monthBucket(e.transactionDate || e.createdAt).expenses += expenseAmount(e); });
      purchases.forEach((p) => { monthBucket(p.purchaseDate || p.createdAt).purchases += purchaseAmount(p); });

      const monthlyRevenue: Record<string, number> = {};
      const monthlyExpenses: Record<string, number> = {};
      const monthlyPurchases: Record<string, number> = {};
      Array.from(monthlyMap.keys()).sort().forEach((sortKey) => {
        const bucket = monthlyMap.get(sortKey)!;
        monthlyRevenue[bucket.label] = bucket.revenue;
        monthlyExpenses[bucket.label] = bucket.expenses;
        monthlyPurchases[bucket.label] = bucket.purchases;
      });

      // Expense categories (from the full expense set)
      const expenseCategories: Record<string, number> = {};
      expenses.forEach((e) => {
        const category = e.category || 'Other';
        expenseCategories[category] = (expenseCategories[category] || 0) + expenseAmount(e);
      });

      // Recent transactions across sales, expenses and purchases
      const mapStatus = (status?: string): 'completed' | 'pending' | 'failed' => {
        const s = (status || '').toUpperCase();
        if (s === 'COMPLETED' || s === 'PAID') return 'completed';
        if (s === 'CANCELLED' || s === 'FAILED' || s === 'REFUNDED') return 'failed';
        return 'pending';
      };

      const recentTransactions: DashboardTransaction[] = [
        ...sales.map((s) => ({
          id: s.id,
          type: 'income' as const,
          amount: saleAmount(s),
          description: `Sale to ${s.buyerName || 'Customer'}`,
          category: 'Sales',
          date: s.saleDate || s.createdAt,
          status: mapStatus(s.status),
        })),
        ...expenses.map((e) => ({
          id: e.id,
          type: 'expense' as const,
          amount: expenseAmount(e),
          description: e.description || `${e.category || 'General'} expense`,
          category: e.category || 'General',
          date: e.transactionDate || e.createdAt,
          status: mapStatus(e.status),
        })),
        ...purchases.map((p) => ({
          id: p.id,
          type: 'expense' as const,
          amount: purchaseAmount(p),
          description: `Purchase from ${p.supplierName || 'Supplier'}`,
          category: 'Purchases',
          date: p.purchaseDate || p.createdAt,
          status: mapStatus(p.status),
        })),
      ]
        .filter((t) => t.date)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 10);

      // Top customers (from the full sales set)
      const customerMap = new Map<string, CustomerAnalytics>();
      sales.forEach((sale) => {
        const key = sale.buyerName || 'Unknown Customer';
        const existing = customerMap.get(key);
        if (existing) {
          existing.totalSpent += saleAmount(sale);
          existing.transactionCount += 1;
          if (new Date(sale.saleDate || sale.createdAt) > new Date(existing.lastPurchase)) {
            existing.lastPurchase = sale.saleDate || sale.createdAt;
          }
        } else {
          customerMap.set(key, {
            id: sale.id,
            name: key,
            totalSpent: saleAmount(sale),
            transactionCount: 1,
            lastPurchase: sale.saleDate || sale.createdAt,
            phone: sale.buyerPhone,
            address: sale.buyerAddress,
          });
        }
      });
      const topCustomers = Array.from(customerMap.values())
        .sort((a, b) => b.totalSpent - a.totalSpent)
        .slice(0, 5);

      // KPIs
      const salesCount = sales.length;
      const avgOrderValue = salesCount > 0 ? totalRevenue / salesCount : 0;

      const revenueSeries = Object.values(monthlyRevenue);
      const currentMonthRevenue = revenueSeries.slice(-1)[0] || 0;
      const previousMonthRevenue = revenueSeries.slice(-2, -1)[0] || 0;
      const revenueGrowth = previousMonthRevenue > 0
        ? ((currentMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100
        : 0;

      const recentMonthlyRevenues = revenueSeries.slice(-3);
      const monthlyRecurring = recentMonthlyRevenues.length > 0
        ? recentMonthlyRevenues.reduce((sum, r) => sum + r, 0) / recentMonthlyRevenues.length
        : 0;

      const kpis = {
        revenueGrowth: Math.round(revenueGrowth * 10) / 10,
        customerAcquisition: customerMap.size,
        avgOrderValue: Math.round(avgOrderValue * 100) / 100,
        monthlyRecurring: Math.round(monthlyRecurring * 100) / 100,
      };

      // Cash flow: money in = sales, money out = expenses + purchases
      const incoming = totalRevenue;
      const outgoing = totalExpenses + totalPurchases;

      return {
        totalRevenue,
        totalExpenses,
        totalPurchases,
        netProfit,
        profitMargin,
        monthlyRevenue,
        monthlyExpenses,
        monthlyPurchases,
        recentTransactions,
        topCustomers,
        expenseCategories,
        cashFlow: {
          incoming,
          outgoing,
          balance: incoming - outgoing,
        },
        kpis,
      };
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
      return emptyDashboard;
    }
  }
};

export default analyticsApi; 